"use client";

import { Beaker, Layers, Shield } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
export default function ClinicalValidation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const materials = [{
    name: "01 Product Evaluation",
    description: "Evidence-Based Product Assessment\nWe conduct structured evaluations by reviewing clinical applications, product differentiation, available scientific evidence, and market relevance to help partners identify technologies with strong clinical and commercial potential.\n",
    tensile: "STATUS: EVALUATION PROCESS\xA0 \xA0",
    temp: "PROFESSIONAL REVIEW",
    Icon: Beaker
  }, {
    name: "02 Clinical & Market Insights",
    description: "Supporting Informed Decisions\nOur preliminary assessments combine clinical insights, product positioning, and market opportunities to support strategic product selection and international business development.",
    tensile: "Market Analysis",
    temp: "STRATEGIC INSIGH",
    Icon: Shield
  }, {
    name: "Strategic Partnerships",
    description: "Connecting Innovation with Opportunity\nWe facilitate collaboration between manufacturers, distributors, and healthcare organizations to create long-term partnerships and expand international market opportunities.",
    tensile: "Global Network",
    temp: "Partnership Development",
    Icon: Layers
  }];
  return <section data-section-id="2254" id="clinicalvalidation" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-default text-xs uppercase tracking-wide text-primary mb-6">OUR APPROACH</p>
            <h2 className="font-default font-bold text-6xl leading-heading text-foreground"><span className="!text-5xl">Evaluating Innovation. <br />Building Trust.</span></h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-sm leading-normal text-muted-foreground max-w-xl mb-8">Innovation alone is not enough. Every opportunity begins with understanding clinical relevance, product differentiation, supporting evidence, and market potential. Through a structured evaluation approach, MS Biotechnology helps partners identify technologies that create lasting value for healthcare professionals and patients.</p>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_lab.webp" alt="Medical lab imagery" className="w-full rounded-lg" />
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {materials.map((material, i) => {
          const Icon = material.Icon;
          return <div data-index={i} key={material.name} className="group relative rounded-lg border border-border bg-card p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow-md hover:shadow-lg" style={{
            backgroundColor: "var(--card)"
          }}>
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300" style={{
              backgroundColor: "var(--accent)"
            }}>
                  <Icon className="h-6 w-6 transition-colors duration-300" strokeWidth={1.5} style={{
                color: "var(--primary)"
              }} />
                </div>

                <h3 className="font-default font-semibold text-2xl text-foreground mb-3">
                  {material.name}
                </h3>

                <p className="text-sm leading-normal text-muted-foreground mb-8">
                  {material.description}
                </p>

                <div className="pt-5 border-t border-border">
                  <p className="font-default text-xs tracking-wider uppercase" style={{
                color: "var(--muted-foreground)"
              }}>
                    <span style={{
                  color: "var(--primary)"
                }}>STATUS:</span>{" "}
                    <span className="text-foreground">{material.tensile}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--primary)"
                }}>LEVEL:</span>{" "}
                    <span className="text-foreground">{material.temp}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
        <div className="mt-12 text-center">
          <Button asChild className="transition-smooth">
            <Link to="/CompliancePage">
              Learn More
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}